import { Component, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { Platform } from '@app/classes';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag, SocketEventTypes } from '@app/enums';
import { ATSJobIntegration } from '@app/models/company/ats-job-integration.model';
import { SettingsIntegration } from '@app/models/company/settings/settings-integration.model';
import { SettingsIntegrationService } from '@app/modules/settings/services';
import { AnalyticService, AuthService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { EmploymentTypeDisplayOptions } from '@app/types/translatables/employment-type-display.options';
import { getFullLocationFromAddress } from '@applicant-tracker/functions/get-full-location-from-address.function';
import { JobPostedEventPayload } from '@applicant-tracker/interfaces/job-posted-event-payload.interface';
import { ViolationItem } from '@applicant-tracker/interfaces/validation-response.interface';
import { ApplicantTrackerService } from '@applicant-tracker/services/applicant-tracker.service';
import { SubscriptionService } from '@applicant-tracker/services/subscription.service';
import { ShowView } from '@common/views';
import { JobPostingUpdateDescriptionForm } from '@forms/applicant-tracker/components';
import { JobIndeedIntegration } from '@models/company/job-indeed-integration.model';
import { JobPostingLocationType } from '@models/recruiting/job-posting-location-type.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './information-show.template.html',
    styleUrls: ['./information-show.style.scss'],
    providers: [ApplicantTrackerService],
})
export class InformationShowView extends ShowView implements OnInit {
    @ViewChild('confirmDeleteDialog')
    confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('descriptionForm') descriptionForm: JobPostingUpdateDescriptionForm;
    isLoading = true;
    jobPosting: JobPosting;
    jobBoardSettings: any;
    indeedIntegration: SettingsIntegration;
    jobIndeedIntegration?: JobIndeedIntegration;
    jobIntegration: ATSJobIntegration;
    isOpenRemoveIntegration = false;
    isOpenAddIntegration = false;
    isOpenJobInformation = false;
    isEditingJobDescription = false;
    isEditingPostingInformation = false;
    integrationDialogText = '';
    activeIntegrations: SettingsIntegration[] = [];
    activeJobIntegrations: ATSJobIntegration[] = [];
    selectedJobIntegration: ATSJobIntegration;
    selectedIntegration: SettingsIntegration;
    isUnableToSponsorJobModalOpen = false;
    indeedValidationMessages: ViolationItem[] = [];
    isIndeedValidationModalOpen = false;
    isIndeedValidationUpdateModalOpen = false;
    proceed = false;
    indeedPublicUrl: string | undefined;
    isIndeedSyncing = false;
    isIndeedFailed = false;
    canEditJobPosting = false;
    jobPostingFullLocation = '';
    isSubscribedToJobPosting = false;

    employmentTypeDisplay = EmploymentTypeDisplayOptions;

    jobBoardIntegrationsCardV2 = false;

    private socketSubscription: Subscription;

    constructor(
        private applicantTrackerService: ApplicantTrackerService,
        private notify: NotifyService,
        private auth: AuthService,
        public route: ActivatedRoute,
        private integrationService: SettingsIntegrationService,
        private analyticService: AnalyticService,
        private socketService: SocketService,
        private subscriptionService: SubscriptionService,
        private featureService: FeatureService
    ) {
        super();
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');

        this.isSubscribedToJobPosting = this.jobPosting.hasSubscription;

        this.updateJobIntegrationVariablesFromJobPosting();

        SettingsIntegration.param('company', this.auth.company.id)
            .with('integrationType')
            .get()
            .then(([integrations]) => {
                integrations.forEach((integration) => {
                    if (integration.integrationType.name === 'indeed') {
                        this.indeedIntegration = integration;
                    }
                });
            });

        this.integrationService.jobSyncedWithIndeed$.pipe(takeUntilDestroyed()).subscribe((atsJobIndeedIntegration) => {
            this.jobIndeedIntegration = atsJobIndeedIntegration;
        });
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.canEditJobPosting = this.auth.can(Platform.recruiting.permission.recruiter);
        this.jobBoardIntegrationsCardV2 = await this.featureService.has(FeatureFlag.jobBoardIntegrationsCardV2);

        if (!this.jobBoardIntegrationsCardV2) {
            await this.loadIntegrations();
        }

        if (!this.jobIntegration) {
            this.jobIntegration = new ATSJobIntegration();
        }

        this.refreshAddressForLinkComponent();

        this.applicantTrackerService
            .getBoardSettings()
            .then((jobBoardSettings) => {
                this.jobBoardSettings = jobBoardSettings;
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));

        const [rawLocationTypes] = await JobPostingLocationType.all();

        this.jobPosting.allLocationTypes = rawLocationTypes;
    }

    async loadIntegrations(): Promise<void> {
        this.activeIntegrations = await this.integrationService.getCompanyActiveIntegrations();
        this.activeJobIntegrations = await this.integrationService.getJobIntegrations(this.jobPosting.id);
    }

    openRemoveIntegrationModal(integration: SettingsIntegration): void {
        this.integrationDialogText = integration.integrationType.label;
        this.isOpenRemoveIntegration = true;
        this.selectedJobIntegration = this.activeJobIntegrations.find((item: ATSJobIntegration) => {
            return integration.id === item.integrationId;
        });
    }

    closeAddIntegrationModal(): void {
        this.isOpenAddIntegration = false;
        this.jobIntegration.isEnabled = false;
    }

    openAddIntegrationModal(integration: SettingsIntegration): void {
        this.integrationDialogText = integration.integrationType.label;
        this.isOpenAddIntegration = true;
        this.selectedJobIntegration = this.activeJobIntegrations.find((item: ATSJobIntegration) => {
            return integration.id === item.integrationId;
        });
        this.selectedIntegration = integration;
    }

    closeRemoveIntegrationModal(): void {
        this.isOpenRemoveIntegration = false;
        this.jobIntegration.isEnabled = true;
    }

    getJobIntegrationIfExists(integration: SettingsIntegration): boolean {
        const jobIntegration = this.activeJobIntegrations.find((item: ATSJobIntegration) => {
            return integration.id === item.integrationId;
        });

        return jobIntegration ? jobIntegration.isEnabled : false;
    }

    async onAddJobIntegration(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingAddedIntegrationJob);

        this.isOpenAddIntegration = false;
        this.isLoading = true;

        // TODO Indeed, new integration - socket events are deprecated
        this.socketSubscription = this.socketService
            .getCompanyChannel([SocketEventTypes.jobPostedOnIndeed, SocketEventTypes.jobPostingFailedOnIndeed])
            .subscribe(async (payload: JobPostedEventPayload) => {
                if (payload.jobPostId !== this.jobPosting.id) {
                    return;
                }

                // Failure case
                if (!payload.status) {
                    this.isIndeedFailed = true;
                    this.socketSubscription.unsubscribe();
                    return;
                }

                const jobPosting = await JobPosting.param('company', this.auth.company.id)
                    .with(['indeedIntegration', 'jobIntegrations.integration.integrationType'])
                    .find(this.jobPosting.id);

                /**
                 * This is expecting to find indeed related data.
                 * Once support for linkedin is live and users link with linkedin, this function won't work as expected and we
                 * will need to specifically target indeed integrations.
                 *
                 * @deprecated and will be removed with flag job-board-integrations-card-v2
                 */
                this.jobIntegration = jobPosting.jobIntegrations[0];
                this.jobIndeedIntegration = jobPosting.indeedIntegration;
                this.isIndeedSyncing = false;
                this.indeedPublicUrl = payload.publicUrl;
                this.socketSubscription.unsubscribe();
            });

        try {
            this.isLoading = true;

            if (this.selectedJobIntegration) {
                this.selectedJobIntegration.isEnabled = true;
                await this.selectedJobIntegration.save();
            } else {
                const newJobIntegration = new ATSJobIntegration({
                    atsJobId: this.jobPosting.id,
                    integrationId: this.selectedIntegration.id,
                    isEnabled: true,
                });
                await newJobIntegration.save();
            }

            await this.loadIntegrations();
            this.isLoading = false;
            this.isOpenAddIntegration = false;
            this.isIndeedSyncing = true;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
            this.isOpenAddIntegration = false;
        }
    }

    async onRemoveJobIntegration(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingRemovedIntegrationJob);
        try {
            this.isOpenRemoveIntegration = false;
            this.isLoading = true;
            await this.selectedJobIntegration.delete();
            await this.loadIntegrations();
            this.isLoading = false;
            this.jobIndeedIntegration = undefined;
            this.indeedPublicUrl = undefined;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
            this.isOpenRemoveIntegration = false;
        }
    }

    onIntegrationToggleChange(toggle: MatSlideToggleChange, integration: SettingsIntegration): void {
        toggle.checked ? this.openAddIntegrationModal(integration) : this.openRemoveIntegrationModal(integration);
    }

    onIndeedIntegrationToggleChange(state: boolean): void {
        if (state) {
            this.openAddIntegrationModal(this.indeedIntegration);
            return;
        }

        this.openRemoveIntegrationModal(this.indeedIntegration);
    }

    onSaveJobDescription(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingEditJobDescriptionSave);
        this.onSave();
    }

    async onSave(): Promise<void> {
        if (!this.jobPosting.isDirty()) {
            return;
        }

        if (this.descriptionForm) {
            this.descriptionForm.submit();

            if (!this.descriptionForm.valid) {
                return;
            }
        }

        this.isLoading = true;

        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('applicant-tracker.jobPostingSaved');
                if (this.isEditingPostingInformation) {
                    this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingInformationEditSave);
                }
                this.isLoading = false;
                this.isEditingPostingInformation = false;
                this.isEditingJobDescription = false;

                this.refreshAddressForLinkComponent();

                //We need to refresh the jobPostingLocationType (because after save relationships can't be refreshed)
                this.refreshJobPostingLocationType();
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotSaveJobPosting');
                this.isLoading = false;
                this.isEditingPostingInformation = false;
                this.isEditingJobDescription = false;
            });

        this.proceed = false;
    }

    async refreshJobPostingLocationType(): Promise<void> {
        try {
            const [rawLocationTypes] = await JobPostingLocationType.all();
            const freshJobPostingLocationType = rawLocationTypes.find(
                (locationType) => locationType.id === this.jobPosting.jobPostingLocationTypeId
            );
            this.jobPosting.jobPostingLocationType = freshJobPostingLocationType;
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotRefreshJobPostingLocationType');
        }
    }

    onProceed(): void {
        this.isIndeedValidationUpdateModalOpen = false;
        this.proceed = true;
        this.analyticService.trackEvent(AnalyticEvents.EditPostingInformationErrorConfirm);
        this.onSave();
    }

    async onPause(pause: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.jobPosting = await this.integrationService.pauseJobIntegration(this.jobPosting.id, pause);

            this.updateJobIntegrationVariablesFromJobPosting();

            if (!this.jobBoardIntegrationsCardV2) {
                await this.loadIntegrations();
            }
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    onArchiveChange(archived: boolean): void {
        // archiving a job posting will delete any integrations that exist.
        // Setting undefined so further saves without a reload don't try to validate against indeed.
        this.jobIndeedIntegration = undefined;
    }

    onCancel(): void {
        this.jobPosting.revert();
        this.isEditingPostingInformation = false;
        this.isEditingJobDescription = false;
    }

    /**
     * Implemented through header component
     */
    onDelete(): void {}

    withBreaks(text: string): string {
        return text.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }

    isIndeedConnect(): boolean {
        return this.indeedIntegration !== undefined && this.indeedIntegration.isEnabled;
    }

    onCancelIndeedIntegration(): void {
        this.isIndeedValidationModalOpen = false;
        this.jobIntegration.isEnabled = false;
        this.analyticService.trackEvent(AnalyticEvents.PostingJobToIndeedErrorDuringIntegrationToggle);
    }

    closeUnableSponsorJobDialog(): void {
        this.isUnableToSponsorJobModalOpen = false;
        this.analyticService.trackEvent(AnalyticEvents.NoPaymentMethodOnAccount);
    }

    onClosingIndeedValidationUpdateModal(): void {
        this.isIndeedValidationUpdateModalOpen = false;
        this.analyticService.trackEvent(AnalyticEvents.EditPostingInformationErrorCancel);
    }

    goToPostingLink(postingLink: string): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingClickJobPostingURL);
        window.open(postingLink, '_blank').focus();
    }

    async saveSubscriptionStatus(shouldSubscribe: boolean): Promise<void> {
        try {
            if (shouldSubscribe) {
                await this.subscriptionService.subscribe(this.jobPosting.topic.id);
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingInformationSubscribe);
            } else {
                await this.subscriptionService.unsubscribe(this.jobPosting.topic.id);
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingInformationUnsubscribe);
            }
        } catch (error) {
            // Timeout waits for animation to finish before toggling it back and
            // displaying the error message.
            setTimeout(() => {
                this.isSubscribedToJobPosting = !shouldSubscribe;

                this.notify.error(
                    shouldSubscribe
                        ? 'applicant-tracker.oopsUnableToSubscribeAtThisTime'
                        : 'applicant-tracker.oopsUnableToUnSubscribeAtThisTime'
                );
            }, 200);
        }
    }

    private refreshAddressForLinkComponent(): void {
        this.jobPostingFullLocation = getFullLocationFromAddress(this.jobPosting.address);
    }

    /**
     * This function is expecting to find indeed related data.
     * Once support for linkedin is live and users link with linkedin, this function won't work as expected and we
     * will need to specifically target indeed integrations.
     *
     * @deprecated and will be removed with flag job-board-integrations-card-v2
     */
    private updateJobIntegrationVariablesFromJobPosting(): void {
        if (this.jobPosting.jobIntegrations.length) {
            this.jobIntegration = this.jobPosting.jobIntegrations[0];
            this.jobIndeedIntegration = this.jobPosting.indeedIntegration;
        }
    }
}
